import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Save, ArrowLeft, Calendar, AlertCircle, Clock, Trash2 } from 'lucide-react';
import { SessionDetails as SessionDetailsType, Squad } from '../types';
import { format } from 'date-fns';
import { getSessionById, createSession, updateSession, deleteSession } from '../services/sessionService';
import toast from 'react-hot-toast';

const SessionDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNewSession = !id || id === 'new';

  const emptySession: SessionDetailsType = {
    id: '',
    date: new Date().toISOString().split('T')[0],
    time: '05:00',
    duration: 1,
    location: '',
    type: 'Training',
    groundBooking: 'Full Pitch',
    squads: [],
    attendance: {
      'U11': [],
      'U13': [],
      'U15': [],
      'U17': []
    },
    coaches: {
      'U11': { primary: '', secondary: '' },
      'U13': { primary: '', secondary: '' },
      'U15': { primary: '', secondary: '' },
      'U17': { primary: '', secondary: '' }
    },
    focus: {
      'U11': '',
      'U13': '',
      'U15': '',
      'U17': ''
    },
    documents: [],
    attendanceStatus: 'not-required',
    locked: false
  };

  const [session, setSession] = useState<SessionDetailsType>(emptySession);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    loadSession();
  }, [id]);

  const loadSession = async () => {
    if (!isNewSession) {
      try {
        setIsLoading(true);
        setError(null);
        const sessionData = await getSessionById(id);

        if (sessionData) {
          setSession(sessionData);
        } else {
          setError('Session not found');
        }
      } catch (err) {
        console.error('Error loading session:', err);
        setError('Failed to load session. Please try again.');
      } finally {
        setIsLoading(false);
      }
    } else {
      setSession({
        ...emptySession,
        id: Date.now().toString()
      });
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setSession(prev => ({ ...prev, [name]: value }));
  };

  const handleDurationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    if (!isNaN(value) && value > 0) {
      setSession(prev => ({ ...prev, duration: value }));
    }
  };

  const handleSquadChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = e.target;
    setSession(prev => {
      if (checked) {
        return {
          ...prev,
          squads: [...prev.squads, value as Squad]
        };
      } else {
        return {
          ...prev,
          squads: prev.squads.filter(squad => squad !== value)
        };
      }
    });
  };

  const handleFocusChange = (squadKey: Squad, focus: string) => {
    setSession(prev => {
      const updatedFocus = { ...prev.focus };
      updatedFocus[squadKey] = focus;
      return { ...prev, focus: updatedFocus };
    });
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);

      if (isNewSession) {
        await createSession(session);
        toast.success('Session created successfully');
      } else {
        await updateSession(id, session);
        toast.success('Session updated successfully');
      }

      navigate('/sessions');
    } catch (err) {
      console.error('Error saving session:', err);
      toast.error('Failed to save session');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    try {
      await deleteSession(id);
      toast.success('Session deleted successfully');
      navigate('/sessions');
    } catch (err) {
      console.error('Error deleting session:', err);
      toast.error('Failed to delete session');
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <p className="text-gray-500">Loading session...</p>
        </div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertCircle className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error || 'Session not found'}</p>
              <button
                className="mt-2 text-sm font-medium text-red-700 hover:text-red-600"
                onClick={() => navigate('/sessions')}
              >
                Back to Sessions
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8 pb-24 sm:pb-8">
      <div className="mb-6 sm:mb-8">
        <button
          type="button"
          className="flex items-center text-navy-800 hover:text-navy-900 mb-2"
          onClick={() => navigate('/sessions')}
        >
          <ArrowLeft size={20} className="mr-2" />
          Back to Sessions
        </button>
        <h1 className="text-3xl font-bold text-gray-900">
          {isNewSession ? 'Create New Session' : 'Edit Session'}
        </h1>
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
            <div className="form-group">
              <label htmlFor="date" className="form-label">
                Session Date
              </label>
              <input
                type="date"
                id="date"
                name="date"
                className="form-input min-h-[44px]"
                value={session.date}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="time" className="form-label">
                Session Time
              </label>
              <input
                type="time"
                id="time"
                name="time"
                className="form-input min-h-[44px]"
                value={session.time}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="duration" className="form-label flex items-center">
                <Clock size={16} className="mr-2" />
                Session Duration (hours)
              </label>
              <input
                type="number"
                id="duration"
                name="duration"
                className="form-input min-h-[44px]"
                value={session.duration}
                onChange={handleDurationChange}
                min="0.5"
                max="5"
                step="0.5"
                required
              />
              <p className="mt-1 text-xs text-gray-500">
                Enter duration in hours (e.g., 1.5 for 1 hour 30 minutes)
              </p>
            </div>
          </div>

          <div className="form-group mt-4">
            <label htmlFor="location" className="form-label">
              Session Location
            </label>
            <input
              type="text"
              id="location"
              name="location"
              className="form-input min-h-[44px]"
              value={session.location}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 mt-4">
            <div className="form-group">
              <label htmlFor="type" className="form-label">
                Session Type
              </label>
              <select
                id="type"
                name="type"
                className="form-input min-h-[44px]"
                value={session.type}
                onChange={handleInputChange}
                required
              >
                <option value="Training">Training</option>
                <option value="Tourney Match">Match</option>
                <option value="Practice Match">Practice Match</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="groundBooking" className="form-label">
                Ground Booking
              </label>
              <select
                id="groundBooking"
                name="groundBooking"
                className="form-input min-h-[44px]"
                value={session.groundBooking}
                onChange={handleInputChange}
                required
              >
                <option value="Full Pitch">Full Pitch</option>
                <option value="Half Pitch">Half Pitch</option>
              </select>
            </div>
          </div>

          <div className="form-group mt-4">
            <label className="form-label">Squads in Session</label>
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
              {(['U11', 'U13', 'U15', 'U17'] as Squad[]).map(squad => (
                <div key={squad} className="flex items-center min-h-[44px]">
                  <input
                    id={`squad-${squad}`}
                    name={`squad-${squad}`}
                    type="checkbox"
                    value={squad}
                    checked={session.squads.includes(squad)}
                    onChange={handleSquadChange}
                    className="h-6 w-6 sm:h-5 sm:w-5 text-navy-600 focus:ring-navy-500 border-gray-300 rounded"
                  />
                  <label htmlFor={`squad-${squad}`} className="ml-3 block text-base sm:text-sm text-gray-900 cursor-pointer">
                    {squad}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {session.squads.length > 0 && (
            <div className="mt-6 space-y-6">
              <h3 className="text-lg font-medium text-gray-900">Session Plans</h3>
              {session.squads.map(squad => (
                <div key={squad} className="form-group">
                  <label htmlFor={`focus-${squad}`} className="form-label">
                    {squad} Squad Plan
                  </label>
                  <textarea
                    id={`focus-${squad}`}
                    className="form-input"
                    value={session.focus[squad]}
                    onChange={(e) => handleFocusChange(squad, e.target.value)}
                    placeholder={`Enter session plan for ${squad} squad...`}
                    rows={4}
                  />
                </div>
              ))}
            </div>
          )}

          <div className="fixed bottom-16 sm:bottom-auto sm:relative left-0 right-0 p-4 sm:p-0 bg-white sm:bg-transparent border-t sm:border-0 border-gray-200 z-10 flex justify-between items-center sm:mt-6 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] sm:shadow-none">
            {/* Delete — only when editing */}
            {!isNewSession ? (
              <button
                type="button"
                className="flex items-center px-4 py-2 border border-red-300 text-red-600 rounded-md hover:bg-red-50 transition-colors w-full sm:w-auto justify-center mr-3 sm:mr-0 min-h-[44px]"
                onClick={() => setShowDeleteConfirm(true)}
              >
                <Trash2 size={18} className="sm:mr-2" />
                <span className="hidden sm:inline">Delete Session</span>
              </button>
            ) : <div className="hidden sm:block" />}
            <button
              type="button"
              className="btn-primary flex items-center justify-center w-full sm:w-auto min-h-[44px]"
              onClick={handleSave}
              disabled={isSaving}
            >
              <Save size={20} className="mr-2" />
              {isSaving ? 'Saving...' : 'Save Session'}
            </button>
          </div>
        </div>
      </div>

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-auto p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Delete Session?</h3>
            <p className="text-sm text-gray-500 mb-6">
              This action cannot be undone. All attendance records for this session will also be deleted.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                className="btn-outline"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-md transition-colors"
                onClick={handleDelete}
              >
                Delete Session
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SessionDetails;